import type { CSSProperties, ReactNode } from 'react';

import { IconChevronDownSmall } from '../../core/icons';

const BUBBLE_SHADOW = 'var(--shadow-bubble)';
const BRIDGE_SHADOW_STYLE: CSSProperties = { boxShadow: BUBBLE_SHADOW };

export type BundleCardProps = {
    readonly lead: ReactNode;
    readonly bridge?: ReactNode;
    readonly cascade?: ReactNode;
    readonly className?: string;
};

export function BundleCard({ lead, bridge, cascade, className }: BundleCardProps) {
    return (
        <div className={`flex flex-col gap-4 font-sans ${className ?? ''}`}>
            {lead}
            {bridge}
            {cascade ? (
                <div className="animate-fade-in" style={{ animationDuration: '180ms', animationFillMode: 'both' }}>
                    {cascade}
                </div>
            ) : null}
        </div>
    );
}

export type BundleCardSectionProps = {
    readonly label: string;
    readonly status?: ReactNode;
    /** Header control rendered as a sibling of the toggle, so it can hold its own button without nesting buttons. Only shown while the section is expanded. */
    readonly action?: ReactNode;
    readonly expanded?: boolean;
    readonly onToggle?: () => void;
    readonly children?: ReactNode;
    readonly className?: string;
};

function BundleCardSection({
    label,
    status,
    action,
    expanded = true,
    onToggle,
    children,
    className,
}: BundleCardSectionProps) {
    const isClickable = typeof onToggle === 'function';
    const labelClassName =
        'min-w-0 flex-1 truncate text-left text-body-xs font-medium leading-tight text-text-navigation [text-wrap:auto]';
    return (
        <div
            className={`relative w-full overflow-hidden rounded-xxl border border-border-subtle/30 font-sans shadow-sm ${className ?? ''}`}
        >
            <div
                className={`flex min-h-9 items-center gap-0 bg-surface-subtle py-1 pl-l pr-m transition-colors duration-200 ease ${isClickable ? 'hover:bg-surface-interactive/80' : ''}`}
            >
                {isClickable ? (
                    <button
                        type="button"
                        onClick={onToggle}
                        aria-expanded={expanded}
                        className={`flex min-w-0 flex-1 items-center border-0 bg-transparent p-0 text-left ${labelClassName} font-sans`}
                    >
                        {label}
                    </button>
                ) : (
                    <span className={labelClassName}>{label}</span>
                )}
                <span className="ml-auto flex min-w-0 shrink-0 items-center gap-xs pl-s">
                    {status}
                    {expanded ? action : null}
                    {isClickable ? (
                        <button
                            type="button"
                            onClick={onToggle}
                            aria-label={expanded ? 'Collapse' : 'Expand'}
                            className="flex shrink-0 items-center border-0 bg-transparent p-0 font-sans"
                        >
                            <IconChevronDownSmall
                                size={14}
                                aria-hidden={true}
                                className={`shrink-0 text-icon-muted transition-transform duration-200 ease ${expanded ? 'rotate-180' : ''}`}
                            />
                        </button>
                    ) : null}
                </span>
            </div>
            {expanded && children !== undefined ? <div className="bg-surface-raised px-xl py-l">{children}</div> : null}
        </div>
    );
}

export type BundleCardBridgeProps = {
    readonly children: ReactNode;
    readonly className?: string;
};

function BundleCardBridge({ children, className }: BundleCardBridgeProps) {
    return (
        <div className="flex justify-start pb-0">
            <p
                className={`m-0 w-full max-w-[85%] rounded-xl border border-border-soft/80 bg-surface-raised/80 px-l py-s text-xs leading-[1.5] text-text-heading ${className ?? ''}`}
                style={BRIDGE_SHADOW_STYLE}
            >
                {children}
            </p>
        </div>
    );
}

export type BundleCardBridgeSkeletonProps = {
    readonly className?: string;
};

function BundleCardBridgeSkeleton({ className }: BundleCardBridgeSkeletonProps) {
    return (
        <div className="flex justify-start pb-0">
            <div
                className={`w-full max-w-[85%] rounded-xl border border-border-soft/80 bg-surface-raised/80 px-l py-s ${className ?? ''}`}
                style={BRIDGE_SHADOW_STYLE}
            >
                <div className="w-full max-w-md space-y-1.5">
                    <div className="h-2.5 w-[88%] animate-pulse rounded-sm bg-skeleton-base/80" />
                    <div className="h-2.5 w-[55%] animate-pulse rounded-sm bg-skeleton-base/70" />
                </div>
            </div>
        </div>
    );
}

BundleCard.Section = BundleCardSection;
BundleCard.Bridge = BundleCardBridge;
BundleCard.BridgeSkeleton = BundleCardBridgeSkeleton;
